/**
 * The coach-change dialog's copy (DNKADM-0393 U5), as a pure module.
 *
 * It lives beside `StaffChangeDialog.tsx` rather than inside it for the reason
 * `paymentBadgeFit.ts` lives beside `DayGrid.tsx` (DNKADM-0393 U3): that file keeps
 * exporting only components, so `react-refresh/only-export-components` stays quiet. The
 * strings are shared with the tests, which assert the rendered sentence against the same
 * constant the component renders — so a copy change cannot pass a test that still pins the
 * old wording.
 */

/** Which host is asking. Only the sentence's lead-in verb differs. */
export type StaffChangeAction = 'reschedule' | 'edit';

/** The sentence's lead-in — an Edit-time save is not a reschedule. */
export function leadInFor(action: StaffChangeAction): string {
  return action === 'reschedule' ? 'Rescheduling' : 'Saving this change';
}

/**
 * The server SAID no coach would be assigned (`proposedStaff === null`, which it sends
 * exactly when the service's staff mode is `MEMBER_SELECTS`). Only an explicit null
 * licenses this sentence — it is a claim about the operator's booking.
 */
export const NO_COACH_COPY =
  'No coach would be assigned at that time, so this booking would be left without one.';

/**
 * Nothing readable about the proposed coach (`proposedStaff === undefined`) — the payload
 * was absent or dropped. The server refused because the coach WOULD change, which is all
 * it told us, so that is all this says. Deliberately NOT {@link NO_COACH_COPY}: never
 * being told a coach is not the same as being told there is none, and stating the stronger
 * claim while hiding the resend the server would have accepted is worse than degrading.
 */
export function unknownCoachCopy(action: StaffChangeAction): string {
  return `${leadInFor(action)} would move this booking to a different coach.`;
}
